const REQUIREMENT_GROUPS = [
    { type: 'past_due', label: 'Vençuts' },
    { type: 'currently_due', label: 'Urgents' },
    { type: 'eventually_due', label: 'Pendents' },
];

const isBusinessDocumentRequirement = (requirement) => {
    const normalized = String(requirement || '').toLowerCase();
    if (!normalized) return false;

    if (normalized.startsWith('individual.') || normalized.startsWith('person_')) {
        return false;
    }

    if (normalized.includes('proof_of_registration')) {
        return true;
    }

    if (normalized.startsWith('company.') && normalized.includes('verification') && normalized.includes('document')) {
        return true;
    }

    if (normalized.includes('business') && normalized.includes('document')) {
        return true;
    }

    return normalized.includes('verification.document');
};

// `documents.<document>.signer(.person)`: the person of the Stripe account who
// electronically signed an attestation document (Stripe digital attestation).
const isAttestationSignerRequirement = (requirement) => /^documents\.[a-z_]+\.signer(\.person)?$/.test(String(requirement || ''));

const getRequirementErrors = (requirements) => Array.isArray(requirements?.errors)
    ? requirements.errors
    : [];

const getDisplayRequirementKey = (requirement) => {
    const normalized = String(requirement || '');

    if (normalized.startsWith('directors.') || normalized === 'company.directors_provided') {
        return 'directors';
    }

    return normalized;
};

const getErrorsForRequirement = (requirements, requirement) => {
    const displayKey = getDisplayRequirementKey(requirement);
    return getRequirementErrors(requirements).filter(error => {
        return getDisplayRequirementKey(error?.requirement) === displayKey;
    });
};

const extractMissingDirectors = (reason) => {
    const text = String(reason || '').trim();
    const marker = 'The missing directors we have identified are:';
    const markerIndex = text.toLowerCase().indexOf(marker.toLowerCase());

    if (markerIndex === -1) {
        return null;
    }

    return text
        .slice(markerIndex + marker.length)
        .trim()
        .replace(/\.$/, '');
};

const formatStripeErrorReason = (error) => {
    const reason = String(error?.reason || '').trim();
    if (!reason) {
        return null;
    }

    const missingDirectors = extractMissingDirectors(reason);
    if (missingDirectors) {
        return `Stripe ha identificat aquests directors pendents: ${missingDirectors}.`;
    }

    if (/business has directors that have not been added/i.test(reason)) {
        return 'Stripe ha identificat directors de l\'entitat que encara no consten al compte.';
    }

    if (/unable to find information based on the data entered/i.test(reason)) {
        return 'Stripe no ha pogut verificar la informació amb les dades introduïdes. Revisa que les dades siguin correctes a Stripe.';
    }

    if (/document isn't an acceptable verification document/i.test(reason)) {
        return 'El document no és vàlid per a la verificació. Puja un document acceptat directament a Stripe.';
    }

    return reason;
};

const getRegistryRequirementDetail = (requirement) => {
    if (getDisplayRequirementKey(requirement) === 'directors') {
        return 'Els «directors» que demana Stripe són els membres de la junta. Ves a «Registre» per registrar la junta vigent i seguir els passos per declarar-la a Stripe.';
    }

    if (requirement === 'company.tax_id') {
        return 'A «Registre» trobaràs ajuda per obtenir la documentació que acredita l’associació. Comprova que el NIF i les dades del compte de Stripe coincideixin amb els documents.';
    }

    if (isAttestationSignerRequirement(requirement)) {
        return 'Stripe demana indicar quina persona del compte ha signat el document. S’indica al formulari de Stripe en pujar-lo i ha de ser algú declarat al compte, com un membre de la junta registrada a «Registre».';
    }

    if (isBusinessDocumentRequirement(requirement)) {
        return 'Ves a «Registre» per saber com obtenir els documents que acrediten la inscripció de l’associació. Després, aporta a Stripe el document que et demani, amb les mateixes dades del compte.';
    }

    return null;
};

const getRequirementDetail = (requirements, requirement) => {
    const registryDetail = getRegistryRequirementDetail(requirement);
    if (registryDetail) {
        const missingDirectors = getDisplayRequirementKey(requirement) === 'directors'
            ? getErrorsForRequirement(requirements, requirement)
                .map(error => extractMissingDirectors(error?.reason))
                .find(Boolean)
            : null;
        return missingDirectors
            ? `${registryDetail} Membres pendents a Stripe: ${missingDirectors}.`
            : registryDetail;
    }

    const details = getErrorsForRequirement(requirements, requirement)
        .map(formatStripeErrorReason)
        .filter(Boolean);

    return Array.from(new Set(details))[0] || null;
};

export const getPendingStripeRequirements = (requirements) => {
    const items = [];

    REQUIREMENT_GROUPS.forEach(({ type }) => {
        if (Array.isArray(requirements?.[type])) {
            items.push(...requirements[type]);
        }
    });

    getRequirementErrors(requirements).forEach(error => {
        if (error?.requirement) {
            items.push(error.requirement);
        }
    });

    return Array.from(new Set(items.filter(Boolean)));
};

export const getGroupedPendingStripeRequirements = (requirements) => {
    const seen = new Set();

    return REQUIREMENT_GROUPS
        .map(group => {
            const items = Array.isArray(requirements?.[group.type])
                ? requirements[group.type].filter(Boolean)
                : [];

            if (group.type === 'currently_due') {
                getRequirementErrors(requirements).forEach(error => {
                    if (error?.requirement) {
                        items.push(error.requirement);
                    }
                });
            }

            const uniqueItems = [];
            Array.from(new Set(items)).forEach(requirement => {
                if (!seen.has(requirement)) {
                    seen.add(requirement);
                    uniqueItems.push(requirement);
                }
            });

            return {
                ...group,
                items: uniqueItems,
            };
        })
        .filter(group => group.items.length > 0);
};

export const hasPendingStripeRequirements = (requirements) => {
    return getPendingStripeRequirements(requirements).length > 0;
};

export const hasStripeRegistryRequirements = (requirements) => {
    return getPendingStripeRequirements(requirements).some(requirement => getRegistryRequirementDetail(requirement) !== null);
};

export const hasBusinessVerificationDocumentRequirement = (requirements) => {
    const pendingRequirements = getPendingStripeRequirements(requirements);
    if (pendingRequirements.some(isBusinessDocumentRequirement)) {
        return true;
    }

    return getRequirementErrors(requirements).some(error => {
        const reason = String(error?.reason || error?.code || '').toLowerCase();
        return reason.includes('business verification document') ||
            (reason.includes('government records') && reason.includes('document'));
    });
};

export const formatStripeDeadline = (requirements, fallback = null) => {
    if (!requirements?.current_deadline) {
        return fallback;
    }

    const deadline = new Date(requirements.current_deadline * 1000);
    if (Number.isNaN(deadline.getTime())) {
        return fallback;
    }

    const monthNames = [
        'gener',
        'febrer',
        'març',
        'abril',
        'maig',
        'juny',
        'juliol',
        'agost',
        'setembre',
        'octubre',
        'novembre',
        'desembre',
    ];

    return `${deadline.getDate()} de ${monthNames[deadline.getMonth()]} de ${deadline.getFullYear()}`;
};

export const formatStripeRequirement = (requirement) => {
    const translations = {
        directors: 'Registrar els membres de la junta',
        'directors.first_name': 'Nom dels directors de l\'entitat',
        'directors.last_name': 'Cognoms dels directors de l\'entitat',
        'directors.email': 'Email dels directors de l\'entitat',
        'directors.phone': 'Telèfon dels directors de l\'entitat',
        'representative.first_name': 'Nom del representant legal',
        'representative.last_name': 'Cognoms del representant legal',
        'representative.email': 'Email del representant legal',
        'representative.phone': 'Telèfon del representant legal',
        'individual.first_name': 'Nom del representant',
        'individual.last_name': 'Cognoms del representant',
        'individual.dob.day': 'Data de naixement',
        'individual.dob.month': 'Data de naixement',
        'individual.dob.year': 'Data de naixement',
        'individual.address.line1': 'Adreça',
        'individual.address.city': 'Ciutat',
        'individual.address.postal_code': 'Codi postal',
        'individual.email': 'Email',
        'individual.phone': 'Telèfon',
        'individual.id_number': 'DNI/NIE',
        'company.name': 'Nom legal de l\'entitat',
        'company.tax_id': 'NIF de l\'entitat',
        'company.address.line1': 'Adreça fiscal de l\'entitat',
        'company.address.city': 'Ciutat de l\'entitat',
        'company.address.postal_code': 'Codi postal de l\'entitat',
        'company.address.state': 'Província de l\'entitat',
        'business_profile.url': 'Pàgina web',
        'business_profile.mcc': 'Categoria de negoci',
        'business_profile.product_description': 'Descripció de l\'activitat',
        'company.phone': 'Telèfon de l\'entitat',
        'company.verification.document': 'Document de verificació de l\'entitat',
        'company.verification.document.front': 'Document de verificació de l\'entitat',
        'company.verification.document.back': 'Document de verificació de l\'entitat',
        'company.verification.additional_document': 'Document addicional de l\'entitat',
        'company.verification.additional_document.front': 'Document addicional de l\'entitat',
        'company.verification.additional_document.back': 'Document addicional de l\'entitat',
        'company.proof_of_registration': 'Certificat d\'inscripció de l\'entitat',
        'documents.proof_of_registration.files': 'Certificat d\'inscripció de l\'entitat',
        // Digital attestation (Stripe API 2025-12-15+): who electronically signed the document
        'documents.proof_of_registration.signer': 'Persona que signa el certificat d\'inscripció de l\'entitat',
        'documents.proof_of_registration.signer.person': 'Persona que signa el certificat d\'inscripció de l\'entitat',
        'documents.proof_of_ultimate_beneficial_ownership.files': 'Document de titularitat real de l\'entitat',
        'documents.proof_of_ultimate_beneficial_ownership.signer': 'Persona que signa el document de titularitat real',
        'documents.proof_of_ultimate_beneficial_ownership.signer.person': 'Persona que signa el document de titularitat real',
        'external_account': 'Compte bancari (IBAN)',
        'tos_acceptance.date': 'Acceptar termes de servei',
        'tos_acceptance.ip': 'Acceptar termes de servei',
    };

    if (translations[requirement]) {
        return translations[requirement];
    }

    const personMatch = String(requirement || '').match(/^person_[^.]+\.(.+)$/);
    if (personMatch) {
        const field = personMatch[1];
        const personTranslations = {
            email: 'Email del representant',
            phone: 'Telèfon del representant',
            nationality: 'Nacionalitat del representant',
            first_name: 'Nom del representant',
            last_name: 'Cognoms del representant',
            'dob.day': 'Data de naixement del representant',
            'dob.month': 'Data de naixement del representant',
            'dob.year': 'Data de naixement del representant',
            id_number: 'DNI/NIE del representant',
        };
        return personTranslations[field] || `Representant: ${field}`;
    }

    return requirement;
};

export const getGroupedPendingStripeRequirementMessages = (requirements) => {
    const seenDisplayKeys = new Set();

    return getGroupedPendingStripeRequirements(requirements)
        .map(group => {
            const items = [];

            group.items.forEach(requirement => {
                const key = getDisplayRequirementKey(requirement);
                if (seenDisplayKeys.has(key)) {
                    return;
                }

                seenDisplayKeys.add(key);
                items.push({
                    key,
                    label: formatStripeRequirement(key),
                    detail: getRequirementDetail(requirements, requirement),
                });
            });

            return {
                ...group,
                items,
            };
        })
        .filter(group => group.items.length > 0);
};
